import pick from 'lodash/pick';
import isEmpty from 'lodash/isEmpty';
import { DateTime } from 'luxon';

import {
  Reservation,
  ReservationValidation,
  ReservationValidations,
  TripLegType,
  TripLeg,
  TripLegAddress,
  TripLegOverride,
  OnHoldTripLegApproveDenyAction,
  ReservationStatus,
  ReservationPhone,
  StandingOrder,
  StandingOrderReturnType,
  Vehicle,
  SyncedTripLeg,
  GurneyInfo,
} from '@/data/models/reservation.model';
import { TripLegStatus } from '@/data/models/trip-leg-status.model';
import { Passenger } from '@/data/models/passenger.model';
import {
  TransportationAttribute,
  TransportationAttributeGroup,
} from '@/data/models/transportation-attribute.model';
import { TimeZone } from '@/data/models/time-zone.model';
import { Driver } from '@/data/models/driver.model';

import dateUtils from '@/services/DateUtils';
import { phoneUtils } from '@/services/phoneUtils';

import { NEXT_TWO_DAYS_APPOINTMENT_NOT_ALLOWED_PORTAL_NAMES } from '@/data/constants';
import { TransportTripLeg } from '@/data/models/transport-reservation.model';

interface TripLegData {
  TripPurpose: string;
  WillCall: boolean;
  AppointmentDate?: string;
  AppointmentTime: DateTime | null;
  AppointmentTimeZone: string;
  PickupTime: DateTime | null;
  PickupTimeZone: string;
  PickupAddress: string;
  PickupAddressStruct: any;
  PickupPhone: string;
  PickupPhoneExtension: string;
  DropoffAddress: string;
  DropoffAddressStruct: any;
  DropoffPhone: string;
  DropoffPhoneExtension: string;
  RoundTripWillCall: boolean;
  RoundTripReturnTime: DateTime | null;
  RoundTripReturnTimeZone: string;
  PassengerUUID: string;
  ReservationReferenceID: string;
  TripLegReferenceID: string;
  RoundTripTripLegReferenceID: string;
}

interface MemberData {
  AlternateContact: boolean;
  AlternateContactType: string;
  AlternateContactFirstName: string;
  AlternateContactLastName: string;
  AlternateContactPhone: string;
  AlternateContactAllowSMS: boolean;
  AdditionalPassengers: boolean;
  AdditionalPassengerCount: number | '';
  PassengerNoteTypes: string[];
  DriverNoteTypes: string[];
  RequestedTransportationProviderID: number | null;
  ShowOtherInputs: boolean;
  ShowOtherAttributes: boolean;
}

interface OverrideInfo {
  firstName: string;
  lastName: string;
  reason: string;
  servicePlanUUID: string;
}

class ReservationUtils {
  private cancelableStates = [
    TripLegStatus.Draft,
    TripLegStatus.Open,
    TripLegStatus.OnHold,
    TripLegStatus.Offered,
    TripLegStatus.Accepted,
    TripLegStatus.Confirmed,
    TripLegStatus.InTransit,
    TripLegStatus.Onsite,
  ];

  private activatableStates = [
    TripLegStatus.Open,
    TripLegStatus.Offered,
    TripLegStatus.Accepted,
    TripLegStatus.Confirmed,
  ];

  private inProgressStates = [
    TripLegStatus.InTransit,
    TripLegStatus.Onsite,
    TripLegStatus.Onboard,
  ];

  public MobilityAttributeBC = 'TRANSPORTMOBILITY';
  public AmbulatoryAttributeBC = 'AMBULATORY';

  public isRoundTrip(reservation: Reservation): boolean {
    return (
      reservation.tripLegs.length === 2 &&
      reservation.tripLegs[0].metadata?.roundTrip
    );
  }

  public isTripLegCancelable(tripLeg: TripLeg): boolean {
    return !!(
      tripLeg.status &&
      this.cancelableStates.includes(
        tripLeg.status.toLocaleLowerCase() as TripLegStatus,
      )
    );
  }

  public isTripLegActivatable(tripLeg: TripLeg): boolean {
    return !!(
      tripLeg.status &&
      this.activatableStates.includes(
        tripLeg.status.toLocaleLowerCase() as TripLegStatus,
      )
    );
  }

  public isTripLegInProgress(tripLeg: TripLeg): boolean {
    return !!(
      tripLeg.status &&
      this.inProgressStates.includes(
        tripLeg.status.toLocaleLowerCase() as TripLegStatus,
      )
    );
  }

  public isReservationCancelable(reservation: Reservation): boolean {
    let isCancelable = true;
    reservation.tripLegs.forEach((leg) => {
      isCancelable &&= this.isTripLegCancelable(leg);
    });
    return isCancelable;
  }

  private getDisplayAddress(address?: TripLegAddress): any {
    if (address?.addressLine1) {
      const main_text = `${address?.addressLine1}`;
      const secondary_text = `${address?.city}, ${address?.stateCode}, USA`;
      return {
        description: `${main_text}, ${secondary_text}`,
        zipCode: address?.zipCode || '',
        place_id: address?.placeID || '',
      };
    }
    return '';
  }

  public getPassengerPhone(passenger: Passenger): ReservationPhone | undefined {
    const passengerPhone =
      phoneUtils.getPassengerMobilePhone(passenger?.passengerPhones) ??
      phoneUtils.getPassengerHomePhone(passenger?.passengerPhones) ??
      phoneUtils.getPassengerNAPhone(passenger?.passengerPhones);
    if (passengerPhone) {
      return {
        phoneNumberTypeBC: 'NA',
        passengerPhoneUseBC: 'RESERVATION',
        phoneNumber: passengerPhone.phoneNumber,
        phoneExtension: passengerPhone.phoneExtension,
        hasSMSConsent: passengerPhone.hasSMSConsent,
      };
    }

    return undefined;
  }

  public getSecondaryAttributeDefaults(
    attributes?: TransportationAttribute[],
    leg?: TripLeg,
  ) {
    const secondaryAttributes: any = {};
    attributes?.forEach((a) => {
      if (
        a.grouping === TransportationAttributeGroup.SECONDARY &&
        a.isPassengerSelectable
      ) {
        const ctrlName = `SecondaryAttribute_${a.transportationAttributeUUID}`;
        const idx =
          leg?.transportationAttributes?.findIndex((attr) => {
            return attr[0] === a.transportationAttributeUUID;
          }) || -1;
        let isBoolean = !a.childTransportationAttributeCategoryBC;
        secondaryAttributes[ctrlName] = isBoolean
          ? idx >= 0
          : idx >= 0
            ? leg?.transportationAttributes[idx][1]
            : '';
      }
    });

    return secondaryAttributes;
  }

  public getPrimaryAttributeDefaults(
    leg?: TripLeg,
    transportationAttributes?: TransportationAttribute[],
  ): any {
    const mobilityAttrs = leg?.transportationAttributes[0] || [];
    const mobilityDefault = transportationAttributes?.find(
      (ta) =>
        ta.transportationAttributeCategoryBC === this.MobilityAttributeBC &&
        ta.isPassengerSelectable &&
        ta.isDefault,
    );
    const attrs = leg?.transportationAttributes[1] || [];
    const ambulatoryDefault = transportationAttributes?.find(
      (ta) =>
        ta.transportationAttributeCategoryBC === this.AmbulatoryAttributeBC &&
        ta.grouping === TransportationAttributeGroup.PRIMARY &&
        ta.isPassengerSelectable &&
        ta.isDefault,
    );
    const attributeLevel1 =
      attrs?.[0] ?? (ambulatoryDefault?.transportationAttributeUUID || '');
    return {
      MobilityAttribute:
        mobilityAttrs[0] ||
        (leg ? mobilityDefault?.transportationAttributeUUID : '') ||
        '',
      passengerMobility: attributeLevel1,
      AttributeLevel1: attributeLevel1,
      AttributeLevel2: attrs?.[1] ?? '',
      AttributeLevel3: attrs?.[2] ?? '',
      AttributeLevel4:
        attrs?.[3] ??
        leg?.gurney?.oxygenCapacityTransportationAttributeUUID ??
        '',
      AttributeLevel5:
        attrs?.[4] ?? leg?.gurney?.stairsCountTransportationAttributeUUID ?? '',
      AttributeLevel6: attrs?.[5] ?? '',
    };
  }

  public getMemberDefaults(
    leg?: TripLeg,
    _transportationAttributes?: TransportationAttribute[],
  ): MemberData {
    const altFirstName = leg?.alternateContact?.firstName || '';
    const altLastName = leg?.alternateContact?.lastName || '';
    const altPhone = leg?.alternateContact?.phoneNumber || '';
    const memberData: MemberData = {
      AlternateContact: !!altFirstName,
      AlternateContactType: leg?.alternateContact?.alternateContactTypeBC || '',
      AlternateContactFirstName: altFirstName,
      AlternateContactLastName: altLastName,
      AlternateContactPhone: altPhone,
      AlternateContactAllowSMS: leg?.alternateContact?.hasSMSConsent || false,
      AdditionalPassengers: !!leg?.companionCount || false,
      AdditionalPassengerCount: leg?.companionCount || '',
      PassengerNoteTypes: leg?.tripLegPassengerNoteTypeBCs || [],
      DriverNoteTypes: leg?.tripLegDriverNoteTypeBCs || [],
      RequestedTransportationProviderID:
        leg?.requestedTransportationProviderID || null,
      ShowOtherInputs: false, // TODO: we could set this to true to open up the "other stuff" section in the member form if anything is initialized.
      ShowOtherAttributes: false, // TODO: same as above
    };
    return memberData;
  }

  public getLegDefaults(
    passenger: Passenger,
    reservationReferenceID?: string,
    leg?: TripLeg,
    transportationAttributes?: TransportationAttribute[],
  ): TripLegData {
    const pickupPhone = leg?.pickUpPhone ?? this.getPassengerPhone(passenger);
    const pickUpNote = leg?.pickUpNote;
    const dropoffPhone = leg?.dropOffPhone;
    const dropOffNote = leg?.dropOffNote;
    const attributeDefaults = this.getPrimaryAttributeDefaults(
      leg,
      transportationAttributes,
    );
    const pickupTimeZone = leg?.pickUpAddress.timeZone;
    const apptTimeZone = leg?.dropOffAddress.timeZone;
    const apptTime = DateTime.fromISO(leg?.appointmentDateTime || '', {
      zone: apptTimeZone,
    }).setZone(dateUtils.WORK_TIME_ZONE, { keepLocalTime: true });
    const pickupTime = DateTime.fromISO(leg?.pickUpDueDateTime || '', {
      zone: pickupTimeZone,
    }).setZone(dateUtils.WORK_TIME_ZONE, { keepLocalTime: true });
    let legData: TripLegData = {
      TripPurpose: leg?.purposeCodeUUID || '',
      WillCall: leg?.isWillCall || false,
      AppointmentTime: leg?.appointmentDateTime
        ? apptTime
        : leg?.appointmentDate
          ? DateTime.fromISO(leg.appointmentDate)
              .setZone(dateUtils.WORK_TIME_ZONE)
              .plus({ hours: 11 }) // add 11 hours to default to 8am PT
          : null,
      AppointmentTimeZone: leg?.dropOffAddress?.timeZone || '',
      PickupTime: leg?.pickUpDueDateTime ? pickupTime : null,
      PickupTimeZone: leg?.pickUpAddress?.timeZone || '',
      PickupAddress: this.getDisplayAddress(leg?.pickUpAddress),
      PickupAddressStruct: leg?.pickUpAddress
        ? JSON.stringify(leg.pickUpAddress)
        : undefined,
      PickupPhone: pickupPhone?.phoneNumber ? pickupPhone.phoneNumber : '',
      PickupPhoneExtension: pickupPhone?.phoneExtension || '',
      DropoffAddress: this.getDisplayAddress(leg?.dropOffAddress),
      DropoffAddressStruct: leg?.dropOffAddress
        ? JSON.stringify(leg.dropOffAddress)
        : undefined,
      DropoffPhone: dropoffPhone?.phoneNumber ? dropoffPhone.phoneNumber : '',
      DropoffPhoneExtension: dropoffPhone?.phoneExtension || '',
      RoundTripWillCall:
        typeof leg?.roundTripWillCall == 'boolean'
          ? leg?.roundTripWillCall
          : true,
      RoundTripReturnTime: leg?.roundTripReturnTime
        ? DateTime.fromISO(leg.roundTripReturnTime, {
            zone: leg?.roundTripReturnTimeZone,
          }).setZone(dateUtils.WORK_TIME_ZONE)
        : null,
      RoundTripReturnTimeZone: leg?.roundTripReturnTimeZone || '',
      PassengerUUID: passenger.passengerUUID || '',
      ReservationReferenceID: reservationReferenceID || '',
      TripLegReferenceID: leg?.tripLegReferenceID || '',
      RoundTripTripLegReferenceID: leg?.roundTripTripLegReferenceID || '',
      height: leg?.gurney?.memberHeightInches || '',
      weight: leg?.gurney?.memberWeightLbs || '',
      AttributeLevel2: leg?.gurney?.gurneyTypeTransportationAttributeUUID || '',
      AttributeLevel4:
        leg?.gurney?.oxygenCapacityTransportationAttributeUUID || '',
      AttributeLevel5:
        leg?.gurney?.stairsCountTransportationAttributeUUID || '',
      ...(pickUpNote ? { PickupNote: pickUpNote } : {}),
      ...(dropOffNote ? { DropoffNote: dropOffNote } : {}),
      ...attributeDefaults,
    };
    return legData;
  }

  public getDefaults(
    passenger: Passenger,
    reservation: Reservation | null,
    transportationAttributes: TransportationAttribute[],
    tripLeg?: TripLeg,
  ): any {
    const tripType = reservation ? TripLegType.MultiLeg : TripLegType.RoundTrip;
    const homePhone = phoneUtils.getPassengerHomePhone(
      passenger.passengerPhones,
    );
    const mobilePhone = phoneUtils.getPassengerMobilePhone(
      passenger.passengerPhones,
    );
    const memberDefaults = this.getMemberDefaults(
      undefined,
      transportationAttributes,
    );
    const reservationData = {
      TripType: tripType,
      MultiLegTrips: [
        this.getLegDefaults(
          passenger,
          reservation?.reservationReferenceID || undefined,
          tripLeg,
          transportationAttributes,
        ),
      ],
      ...memberDefaults,
      MemberHomePhone: homePhone?.phoneNumber || '',
      MemberMobilePhone: mobilePhone?.phoneNumber || '',
      AllowText: mobilePhone?.hasSMSConsent || false,
      OverrideFirstName: '',
      OverrideLastName: '',
      OverrideReason: '',
      OverrideServicePlan: '',

      // Standing order
      StandingOrder: false,
      StandingOrderStartDate: null,
      StandingOrderEndDate: null,
      StandingOrderMonday: false,
      StandingOrderTuesday: false,
      StandingOrderWednesday: false,
      StandingOrderThursday: false,
      StandingOrderFriday: false,
      StandingOrderSaturday: false,
      StandingOrdeSunday: false,
      StandingOrderMondayAppointmentTime: null,
      StandingOrderTuesdayAppointmentTime: null,
      StandingOrderWednesdayAppointmentTime: null,
      StandingOrderThursdayAppointmentTime: null,
      StandingOrderFridayAppointmentTime: null,
      StandingOrderSaturdayAppointmentTime: null,
      StandingOrderSundayAppointmentTime: null,
      StandingOrderMondayPickupTime: null,
      StandingOrderTuesdayPickupTime: null,
      StandingOrderWednesdayPickupTime: null,
      StandingOrderThursdayPickupTime: null,
      StandingOrderFridayPickupTime: null,
      StandingOrderSaturdayPickupTime: null,
      StandingOrderSundayPickupTime: null,
    };
    return reservationData;
  }

  private getPhone(
    legData: any,
    pickup: boolean,
  ): ReservationPhone | undefined {
    const num = pickup ? legData.PickupPhone : legData.DropoffPhone;
    const ext = pickup
      ? legData.PickupPhoneExtension
      : legData.DropoffPhoneExtension;
    return num
      ? {
          phoneNumberTypeBC: 'NA',
          passengerPhoneUseBC: 'RESERVATION',
          phoneNumber: `${num.replace(/\s/g, '')}`,
          phoneExtension: ext,
          hasSMSConsent: true,
          // TODO: we set hasSMSConsent to true to fix F1V2-105 but we really need to add a checkbox
          //       in the UI for it but we'll have to get a new UI design and PO approval for that
        }
      : undefined;
  }

  private getSecondaryAttributes(formData: any): string[][] {
    const prefix = `SecondaryAttribute_`;
    const attributeBCs: string[][] = [];
    Object.keys(formData).forEach((key) => {
      if (key.startsWith(prefix)) {
        const attrName = key.substring(prefix.length);
        const val = formData[key];
        if (val) {
          if (typeof val === 'boolean') {
            attributeBCs.push([attrName]);
          } else if (typeof val === 'string') {
            attributeBCs.push([attrName, val]);
          }
        }
      }
    });

    return attributeBCs;
  }

  private copySecondaryAttributeFieldsFromForm(
    legacyFormData: Record<string, unknown>,
    formValues: Record<string, unknown>,
  ): void {
    for (const key of Object.keys(formValues)) {
      if (key.startsWith('SecondaryAttribute_')) {
        legacyFormData[key] = formValues[key];
      }
    }
  }

  private normalizeAppointmentDateValue(
    value: unknown,
    zone: string,
  ): string | undefined {
    if (value instanceof DateTime && value.isValid) {
      return (
        value.setZone(zone, { keepLocalTime: true }).toISODate() || undefined
      );
    }
    if (typeof value === 'string' && value.trim()) {
      const trimmed = value.trim();
      const parsed = DateTime.fromISO(trimmed, { zone });
      return parsed.isValid ? parsed.toISODate() || undefined : trimmed;
    }
    return undefined;
  }

  private getPrimaryAttributes(legData: any): string[] {
    const attributeBCs: string[] = [];
    let idx = 1;
    let attr: string = legData[`AttributeLevel${idx}`];
    while (attr) {
      attributeBCs.push(attr);
      attr = legData[`AttributeLevel${++idx}`];
    }
    return attributeBCs;
  }

  private getAttributes(legData: any, formData: any): string[][] {
    let attrs: string[][] = [];
    let mobilityAttr = legData['MobilityAttribute'];
    if (mobilityAttr) {
      attrs.push([mobilityAttr]);
    }
    const primaryAttrs = this.getPrimaryAttributes(legData);
    if (primaryAttrs.length) {
      attrs.push(primaryAttrs);
    }
    return [...attrs, ...this.getSecondaryAttributes(formData)];
  }

  public getTripLegFromData(
    passengerUUID: string,
    formData: any,
    legData: any,
    index: number,
    overrides: TripLegOverride[],
    overrideInfo: OverrideInfo | null,
    gurneyData?: GurneyInfo,
  ): TripLeg {
    const pickUpAddress: TripLegAddress = JSON.parse(
      legData.PickupAddressStruct || '{}',
    );
    if (legData.PickupTimeZone && !isEmpty(pickUpAddress)) {
      pickUpAddress.timeZone = legData.PickupTimeZone;
    }
    const dropOffAddress: TripLegAddress = JSON.parse(
      legData.DropoffAddressStruct || '{}',
    );
    if (legData.AppointmentTimeZone && !isEmpty(dropOffAddress)) {
      dropOffAddress.timeZone = legData.AppointmentTimeZone;
    }
    const pickupPhone = this.getPhone(legData, true);
    const dropoffPhone = this.getPhone(legData, false);
    const pickUpNote = legData.PickupNote || '';
    const dropOffNote = legData.DropoffNote || '';
    const appointmentTimeZone = legData.AppointmentTimeZone;
    const appointmentTime = appointmentTimeZone
      ? legData.AppointmentTime?.setZone(appointmentTimeZone, {
          keepLocalTime: true,
        })
          .toUTC()
          .toISO()
      : undefined;
    const pickupTimeZone = !legData.WillCall
      ? legData.PickupTimeZone
      : undefined;
    const pickupTime =
      !legData.WillCall && pickupTimeZone
        ? legData.PickupTime?.setZone(pickupTimeZone, {
            keepLocalTime: true,
          })
            .toUTC()
            .toISO()
        : undefined;
    if (overrideInfo) {
      overrides.push({
        tripLegOverrideReasonBC: overrideInfo.reason,
        clientFirstName: overrideInfo.firstName,
        clientLastName: overrideInfo.lastName,
        servicePlanUUID: overrideInfo.servicePlanUUID,
      });
    }

    const transportationAttributes = this.getAttributes(legData, formData);
    const leg: TripLeg = {
      passengerUUID,
      reservationReferenceID: legData.ReservationReferenceID || undefined,
      tripLegReferenceID: legData.TripLegReferenceID || undefined,
      tripLegSequence: index + 1,
      purposeCodeUUID: legData.TripPurpose,
      appointmentDate: legData.WillCall
        ? legData.AppointmentDate?.trim() ||
          (appointmentTime
            ? DateTime.fromISO(appointmentTime, {
                zone: appointmentTimeZone,
              }).toISODate() || undefined
            : undefined)
        : undefined,
      appointmentDateTime: !legData.WillCall ? appointmentTime : undefined,
      pickUpDueDateTime: pickupTime,
      isWillCall: legData.WillCall,
      companionCount: formData.AdditionalPassengers
        ? formData.AdditionalPassengerCount
        : 0,
      // These next two are because sometimes google places API returns addresses with no city. Onica
      // wants us to pass in a bogus city instead of just leaving them blank for some reason.
      pickUpAddress: pickUpAddress.addressLine1
        ? { ...pickUpAddress, city: pickUpAddress.city || 'x' }
        : pickUpAddress,
      dropOffAddress: dropOffAddress.addressLine1
        ? { ...dropOffAddress, city: dropOffAddress.city || 'x' }
        : dropOffAddress,
      pickUpPhone: pickupPhone,
      dropOffPhone: dropoffPhone,
      ...(pickUpNote ? { pickUpNote: pickUpNote } : {}),
      ...(dropOffNote ? { dropOffNote: dropOffNote } : {}),
      transportationAttributes: transportationAttributes,
      tripLegPassengerNoteTypeBCs: formData.PassengerNoteTypes || [],
      tripLegDriverNoteTypeBCs: formData.DriverNoteTypes || [],
      overrides,
      gurney: gurneyData
        ? {
            gurneyTypeTransportationAttributeUUID:
              gurneyData.gurneyTypeTransportationAttributeUUID,
            memberHeightInches: gurneyData.memberHeightInches,
            memberWeightLbs: gurneyData.memberWeightLbs,
            oxygenCapacityTransportationAttributeUUID:
              gurneyData.oxygenCapacityTransportationAttributeUUID || undefined,
            stairsCountTransportationAttributeUUID:
              gurneyData.stairsCountTransportationAttributeUUID || undefined,
            waitAndReturn: false,
          }
        : undefined,
      metadata: Object.assign({ roundTrip: false }, legData.metadata || {}),
      requestedTransportationProviderID:
        formData.RequestedTransportationProviderID || undefined,
    };
    if (formData.AlternateContact && formData.AlternateContactPhone) {
      leg.alternateContact = {
        alternateContactTypeBC: formData.AlternateContactType,
        firstName: formData.AlternateContactFirstName,
        lastName: formData.AlternateContactLastName,
        phoneNumberTypeBC: 'MOBILE',
        phoneNumber: `${formData.AlternateContactPhone.replace(/\s/g, '')}`,
        hasSMSConsent: formData.AlternateContactAllowSMS,
      };
    }

    return leg;
  }

  public extractTripLegFields(leg: any): TripLeg {
    const fields = [
      'passengerUUID',
      'reservationReferenceID',
      'tripLegReferenceID',
      'tripLegSequence',
      'purposeCodeUUID',
      'appointmentDateTime',
      'pickUpDueDateTime',
      'isWillCall',
      'companionCount',
      'overrides',
      'pickUpAddress',
      'dropOffAddress',
      'pickUpPhone',
      'pickUpNote',
      'dropOffPhone',
      'dropOffNote',
      'alternateContact',
      'transportationAttributes',
      'tripLegPassengerNoteTypeBCs',
      'tripLegDriverNoteTypeBCs',
      'metadata',
      'serviceSegmentUUID',
      'tripDenialReasonID',
      'offeredMiles',
      'actualMiles',
      'billableMiles',
      'waitMinutes',
      'tripLegStatusLog',
      'isRoundTrip',
      'requestedTransportationProviderID',
    ];

    return pick(leg, fields) as TripLeg;
  }

  private getStandingOrderTime(
    formData: any,
    day: string,
    enableReturnOverrides: boolean,
  ) {
    const timeZone =
      formData?.MultiLegTrips[0]?.AppointmentTimeZone || 'America/Los_Angeles';
    let time: any = undefined;
    if (formData[`StandingOrder${day}`]) {
      const reservation = this.getReservationFromFormData('', '', formData, []);
      const timedTripLegs = reservation.tripLegs.filter(
        ({ isWillCall }) => !isWillCall,
      );
      time = {
        dayOfWeek: day.toUpperCase(),
        times: timedTripLegs.map(({ tripLegSequence, pickUpDueDateTime }) => {
          // This is a little kludgy for now because of the way the API works. We are not going
          // to support multi-leg trips for now so we are really only worried about the return
          // trip (tripLegSequence === 2) getting specified in a way that the API can deal with.
          const apTime = formData[`StandingOrder${day}AppointmentTime`];
          const puTime = formData[`StandingOrder${day}PickupTime`];
          if (tripLegSequence === 1) {
            return {
              tripLegSequence,
              appointmentTime: apTime
                .setZone(timeZone, { keepLocalTime: true })
                .toFormat('HH:mm:00ZZ'),
              pickUpTime: puTime
                .setZone(timeZone, { keepLocalTime: true })
                .toFormat('HH:mm:00ZZ'),
            };
          } else if (tripLegSequence === 2) {
            const puLeg2 = DateTime.fromISO(pickUpDueDateTime!).setZone(
              timeZone,
            );
            const diff = apTime.diff(puTime);
            return {
              tripLegSequence,
              appointmentTime: puLeg2.plus(diff).toFormat('HH:mm:00ZZ'),
              pickUpTime: puLeg2
                .setZone(timeZone, { keepLocalTime: true })
                .toFormat('HH:mm:00ZZ'),
            };
          }
        }),
      };
    }
    return time;
  }

  public getStandingOrder(formData: any, enableReturnOverrides: boolean) {
    let standingOrder: any = undefined;
    if (formData.StandingOrder) {
      standingOrder = {
        effectiveStartDate: formData.StandingOrderStartDate?.toISODate(),
        effectiveEndDate: formData.StandingOrderEndDate?.toISODate(),
        schedule: [],
      };
      const monday = this.getStandingOrderTime(
        formData,
        'Monday',
        enableReturnOverrides,
      );
      if (monday) standingOrder.schedule.push(monday);
      const tuesday = this.getStandingOrderTime(
        formData,
        'Tuesday',
        enableReturnOverrides,
      );
      if (tuesday) standingOrder.schedule.push(tuesday);
      const wednesday = this.getStandingOrderTime(
        formData,
        'Wednesday',
        enableReturnOverrides,
      );
      if (wednesday) standingOrder.schedule.push(wednesday);
      const thursday = this.getStandingOrderTime(
        formData,
        'Thursday',
        enableReturnOverrides,
      );
      if (thursday) standingOrder.schedule.push(thursday);
      const friday = this.getStandingOrderTime(
        formData,
        'Friday',
        enableReturnOverrides,
      );
      if (friday) standingOrder.schedule.push(friday);
      const saturday = this.getStandingOrderTime(
        formData,
        'Saturday',
        enableReturnOverrides,
      );
      if (saturday) standingOrder.schedule.push(saturday);
      const sunday = this.getStandingOrderTime(
        formData,
        'Sunday',
        enableReturnOverrides,
      );
      if (sunday) standingOrder.schedule.push(sunday);
    }
    return standingOrder;
  }

  public getReservationFromFormData(
    clientBC: string,
    passengerUUID: string,
    formData: any,
    overrides: TripLegOverride[],
    passengerPhone?: ReservationPhone,
    gurneyData?: GurneyInfo[],
  ): Reservation {
    const reservation: Reservation = {
      clientBC,
      passengerUUID,
      tripLegs: [],
    };
    const legs = formData.MultiLegTrips;
    let overrideInfo: OverrideInfo | null = null;
    if (formData.AllowOverride) {
      overrideInfo = {
        firstName: formData.OverrideFirstName,
        lastName: formData.OverrideLastName,
        reason: formData.OverrideReason,
        servicePlanUUID: formData.OverrideServicePlan,
      };
    }
    legs?.forEach((legData: any, idx: number) => {
      const leg = this.getTripLegFromData(
        passengerUUID,
        formData,
        legData,
        idx,
        overrides,
        overrideInfo,
        gurneyData ? gurneyData[idx] : undefined,
      );
      // If editing the leg, then keep existing metadata.
      if (formData.Metadata) {
        leg.metadata = formData.Metadata;
      }
      reservation.tripLegs.push(leg);
    });

    if (formData.TripType === TripLegType.OneWay) {
      reservation.tripLegs[0].dropOffPhone =
        reservation.tripLegs[0].dropOffPhone ?? passengerPhone;
    } else if (formData.TripType === TripLegType.MultiLeg) {
      reservation.tripLegs.forEach((tripLeg) => {
        tripLeg.dropOffPhone = tripLeg.dropOffPhone ?? passengerPhone;
      });
    } else if (formData.TripType === TripLegType.RoundTrip) {
      const legData = formData.MultiLegTrips[0];
      const startLeg =
        this.getRoundTripStartLeg(reservation) || reservation.tripLegs[0];
      const returnLeg = { ...startLeg };
      startLeg.metadata.roundTrip = true;
      startLeg.gurney = gurneyData ? gurneyData[0] : undefined;
      returnLeg.metadata = {
        roundTrip: true,
        roundTripReturnLeg: true,
      };
      returnLeg.isWillCall = legData.RoundTripWillCall;
      returnLeg.pickUpAddress = startLeg.dropOffAddress;
      returnLeg.dropOffAddress = startLeg.pickUpAddress;
      startLeg.dropOffPhone = startLeg.dropOffPhone ?? passengerPhone;
      returnLeg.pickUpPhone = startLeg.dropOffPhone;
      returnLeg.dropOffPhone = startLeg.pickUpPhone;
      returnLeg.gurney = gurneyData ? gurneyData[1] : undefined;

      if (legData.DropoffNote) {
        returnLeg.pickUpNote = legData.DropoffNote || undefined;
      } else {
        returnLeg.pickUpNote = undefined;
      }

      if (legData.PickupNote) {
        returnLeg.dropOffNote = legData.PickupNote || undefined;
      } else {
        returnLeg.dropOffNote = undefined;
      }

      returnLeg.tripLegReferenceID =
        legData?.RoundTripTripLegReferenceID || undefined;
      const appointmentTimeZone = legData.AppointmentTimeZone;
      if (returnLeg.isWillCall) {
        returnLeg.pickUpDueDateTime = undefined;
        if (returnLeg.appointmentDateTime) {
          returnLeg.appointmentDate = DateTime.fromISO(
            returnLeg.appointmentDateTime,
          )
            .setZone(appointmentTimeZone)
            .toISODate()!;
          delete returnLeg.appointmentDateTime;
        } else {
          returnLeg.appointmentDate = startLeg.appointmentDate;
        }
      } else {
        const pickupTime = legData?.RoundTripReturnTime?.setZone(
          appointmentTimeZone,
          {
            keepLocalTime: true,
          },
        );
        returnLeg.pickUpDueDateTime = pickupTime?.toUTC().toISO() || null;
        // returnLeg.appointmentDateTime = startLeg.appointmentDateTime;
        // The back end fails if we use the real appointmentDateTime for the return leg
        // because the pickup time is after the appointment. For now I just put in the
        // pickup time + 1 hour as the appointment time for the return leg.

        returnLeg.appointmentDateTime =
          pickupTime?.plus({ hours: 1 }).toUTC().toISO() || undefined;
      }
      returnLeg.tripLegSequence = startLeg.tripLegSequence + 1;
      reservation.tripLegs.push(returnLeg);
    }

    return reservation;
  }

  private removeEmptyAttributes(obj: any) {
    const props = Object.getOwnPropertyNames(obj);
    props.forEach((name) => {
      if (typeof obj[name] === 'string') {
        if (!obj[name]) {
          delete obj[name];
        }
      } else if (typeof obj[name] === 'number' && isNaN(obj[name])) {
        delete obj[name];
      } else if (name === 'transportationAttributes') {
        const arr = obj[name];
        if (arr?.length === 1 && !arr[0].length) {
          delete obj[name];
        }
      } else if (name === 'pickUpAddress' || name === 'dropOffAddress') {
        if (Object.keys(obj[name]).length === 0) {
          delete obj[name];
        }
      } else if (obj[name] === undefined || obj[name] === null) {
        delete obj[name];
      }
    });
  }

  /**
   * Builds a payload for POST /validate from the member-portal reservation form shape
   * ({ tripType, trips }) plus per-trip `pickupAddressStruct` / `dropoffAddressStruct`
   * from AddressInput.
   */
  public getPartialReservationFromReservationForm(
    clientBC: string,
    passengerUUID: string,
    formValues: {
      tripType: TripLegType;
      trips: any[];
      PassengerNoteTypes?: string[];
      DriverNoteTypes?: string[];
      RequestedTransportationProviderID?: number | null;
      AdditionalPassengers?: boolean;
      AdditionalPassengerCount?: number | null;
      AlternateContact?: boolean;
      AlternateContactType?: string;
      AlternateContactFirstName?: string;
      AlternateContactLastName?: string;
      AlternateContactPhone?: string;
      AlternateContactAllowSMS?: boolean;
    },
  ): any {
    const mappedMultiLegTrips = formValues.trips.map((trip, idx) =>
      this.mapReservationPortalTripToLegacyLegData(trip, passengerUUID, idx),
    );
    const legacyFormData = {
      TripType: formValues.tripType,
      MultiLegTrips: mappedMultiLegTrips,
      AdditionalPassengers: formValues.AdditionalPassengers ?? false,
      AdditionalPassengerCount: formValues.AdditionalPassengers
        ? (formValues.AdditionalPassengerCount ?? 0)
        : 0,
      PassengerNoteTypes: formValues.PassengerNoteTypes ?? [],
      DriverNoteTypes: formValues.DriverNoteTypes ?? [],
      AlternateContact: formValues.AlternateContact ?? false,
      AlternateContactType: formValues.AlternateContactType ?? '',
      AlternateContactFirstName: formValues.AlternateContactFirstName ?? '',
      AlternateContactLastName: formValues.AlternateContactLastName ?? '',
      AlternateContactPhone: formValues.AlternateContactPhone ?? '',
      AlternateContactAllowSMS: formValues.AlternateContactAllowSMS ?? false,
      RequestedTransportationProviderID:
        formValues.RequestedTransportationProviderID ?? null,
    };
    this.copySecondaryAttributeFieldsFromForm(
      legacyFormData,
      formValues as Record<string, unknown>,
    );
    return this.getPartialReservationFromData(
      clientBC,
      passengerUUID,
      legacyFormData,
    );
  }

  /**
   * Full reservation + standing order for POST /createReservation from the member-portal
   * form shape ({ tripType, trips }).
   */
  public getCreateReservationPayloadFromReservationForm(
    clientBC: string,
    passengerUUID: string,
    formValues: {
      tripType: TripLegType;
      trips: any[];
      PassengerNoteTypes?: string[];
      DriverNoteTypes?: string[];
      RequestedTransportationProviderID?: number | null;
      AdditionalPassengers?: boolean;
      AdditionalPassengerCount?: number | null;
      AlternateContact?: boolean;
      AlternateContactType?: string;
      AlternateContactFirstName?: string;
      AlternateContactLastName?: string;
      AlternateContactPhone?: string;
      AlternateContactAllowSMS?: boolean;
    },
    passengerPhone?: ReservationPhone,
    gurneyData?: GurneyInfo[],
  ): { reservation: Reservation; standingOrder: StandingOrder | undefined } {
    const legacyFormData = this.buildLegacyFormDataFromReservationPortal(
      passengerUUID,
      formValues,
    );
    const reservation = this.getReservationFromFormData(
      clientBC,
      passengerUUID,
      legacyFormData,
      [],
      passengerPhone,
      gurneyData,
    );
    const standingOrder = this.getStandingOrder(legacyFormData, false);
    return { reservation, standingOrder };
  }

  private buildLegacyFormDataFromReservationPortal(
    passengerUUID: string,
    formValues: {
      tripType: TripLegType;
      trips: any[];
      PassengerNoteTypes?: string[];
      DriverNoteTypes?: string[];
      RequestedTransportationProviderID?: number | null;
      AdditionalPassengers?: boolean;
      AdditionalPassengerCount?: number | null;
      AlternateContact?: boolean;
      AlternateContactType?: string;
      AlternateContactFirstName?: string;
      AlternateContactLastName?: string;
      AlternateContactPhone?: string;
      AlternateContactAllowSMS?: boolean;
    },
  ): any {
    const mappedMultiLegTrips = formValues.trips.map((trip, idx) =>
      this.mapReservationPortalTripToLegacyLegData(trip, passengerUUID, idx),
    );
    const legacyFormData: any = {
      TripType: formValues.tripType,
      MultiLegTrips: mappedMultiLegTrips,
      AdditionalPassengers: formValues.AdditionalPassengers ?? false,
      AdditionalPassengerCount: formValues.AdditionalPassengers
        ? (formValues.AdditionalPassengerCount ?? 0)
        : 0,
      PassengerNoteTypes: formValues.PassengerNoteTypes ?? [],
      DriverNoteTypes: formValues.DriverNoteTypes ?? [],
      AlternateContact: formValues.AlternateContact ?? false,
      AlternateContactType: formValues.AlternateContactType ?? '',
      AlternateContactFirstName: formValues.AlternateContactFirstName ?? '',
      AlternateContactLastName: formValues.AlternateContactLastName ?? '',
      AlternateContactPhone: formValues.AlternateContactPhone ?? '',
      AlternateContactAllowSMS: formValues.AlternateContactAllowSMS ?? false,
      RequestedTransportationProviderID:
        formValues.RequestedTransportationProviderID ?? null,
    };
    this.mergePortalStandingOrderIntoLegacyForm(legacyFormData, formValues);
    this.mergePortalOverrideIntoLegacyForm(legacyFormData, formValues);
    this.copySecondaryAttributeFieldsFromForm(
      legacyFormData,
      formValues as Record<string, unknown>,
    );
    return legacyFormData;
  }

  private mergePortalOverrideIntoLegacyForm(
    legacyFormData: any,
    formValues: Record<string, unknown>,
  ): void {
    legacyFormData.AllowOverride = Boolean(formValues.AllowOverride);
    legacyFormData.OverrideFirstName = String(
      formValues.OverrideFirstName ?? '',
    );
    legacyFormData.OverrideLastName = String(formValues.OverrideLastName ?? '');
    legacyFormData.OverrideReason = String(formValues.OverrideReason ?? '');
    legacyFormData.OverrideServicePlan = String(
      formValues.OverrideServicePlan ?? '',
    );
  }

  private mergePortalStandingOrderIntoLegacyForm(
    legacyFormData: any,
    formValues: { trips: any[] },
  ): void {
    const trip0 = formValues.trips[0];
    const dayPairs = [
      ['Monday', 'monday'],
      ['Tuesday', 'tuesday'],
      ['Wednesday', 'wednesday'],
      ['Thursday', 'thursday'],
      ['Friday', 'friday'],
      ['Saturday', 'saturday'],
      ['Sunday', 'sunday'],
    ] as const;
    const standingDefaults: Record<string, unknown> = {
      StandingOrder: false,
      StandingOrderStartDate: null,
      StandingOrderEndDate: null,
      StandingOrderMonday: false,
      StandingOrderTuesday: false,
      StandingOrderWednesday: false,
      StandingOrderThursday: false,
      StandingOrderFriday: false,
      StandingOrderSaturday: false,
      StandingOrderSunday: false,
      StandingOrderMondayAppointmentTime: null,
      StandingOrderTuesdayAppointmentTime: null,
      StandingOrderWednesdayAppointmentTime: null,
      StandingOrderThursdayAppointmentTime: null,
      StandingOrderFridayAppointmentTime: null,
      StandingOrderSaturdayAppointmentTime: null,
      StandingOrderSundayAppointmentTime: null,
      StandingOrderMondayPickupTime: null,
      StandingOrderTuesdayPickupTime: null,
      StandingOrderWednesdayPickupTime: null,
      StandingOrderThursdayPickupTime: null,
      StandingOrderFridayPickupTime: null,
      StandingOrderSaturdayPickupTime: null,
      StandingOrderSundayPickupTime: null,
      StandingOrderMondayReturnType: null,
      StandingOrderTuesdayReturnType: null,
      StandingOrderWednesdayReturnType: null,
      StandingOrderThursdayReturnType: null,
      StandingOrderFridayReturnType: null,
      StandingOrderSaturdayReturnType: null,
      StandingOrderSundayReturnType: null,
      StandingOrderMondayReturnPickupTime: null,
      StandingOrderTuesdayReturnPickupTime: null,
      StandingOrderWednesdayReturnPickupTime: null,
      StandingOrderThursdayReturnPickupTime: null,
      StandingOrderFridayReturnPickupTime: null,
      StandingOrderSaturdayReturnPickupTime: null,
      StandingOrderSundayReturnPickupTime: null,
    };
    Object.assign(legacyFormData, standingDefaults);
    if (!trip0?.standingOrder) {
      return;
    }
    legacyFormData.StandingOrder = true;
    legacyFormData.StandingOrderStartDate =
      trip0.standingOrderStartDate ?? null;
    legacyFormData.StandingOrderEndDate = trip0.standingOrderEndDate ?? null;
    const days = trip0.standingOrderDays || {};
    for (const [legacy, portal] of dayPairs) {
      const d = days[portal] || {};
      legacyFormData[`StandingOrder${legacy}`] = !!d.enabled;
      legacyFormData[`StandingOrder${legacy}AppointmentTime`] =
        d.appointmentTime ?? null;
      legacyFormData[`StandingOrder${legacy}PickupTime`] = d.pickupTime ?? null;
      if (d.enabled) {
        legacyFormData[`StandingOrder${legacy}ReturnType`] =
          trip0.roundTripWillCall
            ? StandingOrderReturnType.willCall
            : StandingOrderReturnType.time;
        legacyFormData[`StandingOrder${legacy}ReturnPickupTime`] =
          trip0.roundTripWillCall
            ? null
            : (trip0.roundTripReturnDateTime ?? null);
      }
    }
  }

  private mapReservationPortalTripToLegacyLegData(
    trip: any,
    passengerUUID: string,
    _index: number,
  ): any {
    const workZone = dateUtils.WORK_TIME_ZONE;
    let pickupTz = workZone;
    let dropTz = workZone;
    try {
      if (
        typeof trip.pickupTimeZone === 'string' &&
        trip.pickupTimeZone.trim()
      ) {
        pickupTz = trip.pickupTimeZone.trim();
      } else if (trip.pickupAddressStruct) {
        const p = JSON.parse(trip.pickupAddressStruct);
        if (p.timeZone) pickupTz = p.timeZone;
      }
      if (
        typeof trip.dropoffTimeZone === 'string' &&
        trip.dropoffTimeZone.trim()
      ) {
        dropTz = trip.dropoffTimeZone.trim();
      } else if (trip.dropoffAddressStruct) {
        const d = JSON.parse(trip.dropoffAddressStruct);
        if (d.timeZone) dropTz = d.timeZone;
      }
    } catch {
      // keep defaults
    }

    let appointmentTime: DateTime | null = null;
    let appointmentDateOnly: string | undefined;
    if (trip.isWillCall) {
      appointmentDateOnly = this.normalizeAppointmentDateValue(
        trip.appointmentDate,
        workZone,
      );
      if (appointmentDateOnly) {
        appointmentTime = DateTime.fromISO(appointmentDateOnly, {
          zone: workZone,
        });
      }
    } else if (
      trip.appointmentDateTime instanceof DateTime &&
      trip.appointmentDateTime.isValid
    ) {
      // Match getLegDefaults + getTripLegFromData: wall clock is normalized to the facility
      // zone first; getTripLegFromData then applies AppointmentTimeZone / PickupTimeZone with
      // keepLocalTime to produce UTC for the API.
      appointmentTime = trip.appointmentDateTime.setZone(workZone, {
        keepLocalTime: true,
      });
    }

    let pickupTime: DateTime | null = null;
    if (
      !trip.isWillCall &&
      trip.pickupDateTime instanceof DateTime &&
      trip.pickupDateTime.isValid
    ) {
      pickupTime = trip.pickupDateTime.setZone(workZone, {
        keepLocalTime: true,
      });
    }

    let roundTripReturnTime: DateTime | null = null;
    if (
      !trip.roundTripWillCall &&
      trip.roundTripReturnDateTime instanceof DateTime &&
      trip.roundTripReturnDateTime.isValid
    ) {
      roundTripReturnTime = trip.roundTripReturnDateTime.setZone(dropTz, {
        keepLocalTime: true,
      });
    }

    return {
      TripPurpose: trip.reasonForTrip ?? '',
      WillCall: Boolean(trip.isWillCall),
      AppointmentDate: appointmentDateOnly,
      AppointmentTime: appointmentTime,
      AppointmentTimeZone: dropTz,
      PickupTime: pickupTime,
      PickupTimeZone: pickupTz,
      PickupAddress: '',
      PickupAddressStruct: trip.pickupAddressStruct || '{}',
      PickupPhone: trip.pickupPhone ?? '',
      PickupPhoneExtension: trip.pickupPhoneExtension ?? '',
      DropoffAddress: '',
      DropoffAddressStruct: trip.dropoffAddressStruct || '{}',
      DropoffPhone: trip.dropoffPhone ?? '',
      DropoffPhoneExtension: trip.dropoffPhoneExtension ?? '',
      RoundTripWillCall: Boolean(trip.roundTripWillCall),
      RoundTripReturnTime: roundTripReturnTime,
      RoundTripReturnTimeZone: roundTripReturnTime ? dropTz : '',
      PassengerUUID: passengerUUID,
      ReservationReferenceID: '',
      TripLegReferenceID: '',
      RoundTripTripLegReferenceID: '',
      height: trip.height ?? '',
      weight: trip.weight ?? '',
      AttributeLevel1: trip.AttributeLevel1 ?? '',
      AttributeLevel2: trip.AttributeLevel2 ?? '',
      AttributeLevel3: trip.AttributeLevel3 ?? '',
      AttributeLevel4: trip.AttributeLevel4 ?? '',
      AttributeLevel5: trip.AttributeLevel5 ?? '',
      AttributeLevel6: trip.AttributeLevel6 ?? '',
      MobilityAttribute: trip.MobilityAttribute ?? '',
      PickupNote: trip.pickupNotes ?? '',
      DropoffNote: trip.dropoffNotes ?? '',
    };
  }

  public getPartialReservationFromData(
    clientBC: string,
    passengerUUID: string,
    formData: any,
  ): any {
    const reservation: any = this.getReservationFromFormData(
      clientBC,
      passengerUUID,
      formData,
      [],
    );

    // for now the /validate endpoint doesn't take standing order info
    // so I delete it.
    // TODO: put remove this when the backend is fixed
    delete reservation.standingOrder;

    this.removeEmptyAttributes(reservation);
    reservation.tripLegs.forEach((leg: any) => {
      this.removeEmptyAttributes(leg);
    });

    return reservation;
  }

  private formatConflictErrorMessage(
    message: string,
    t: Function,
    timeZone?: string,
  ): string {
    const reservationIdMatch = message.match(/reservation\s+([A-Z0-9]+)/i);
    const timeMatch = message.match(
      /pickup time\s+([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\.[0-9]+)?Z?)/i,
    );

    if (reservationIdMatch && timeMatch) {
      const reservationId = reservationIdMatch[1];
      const isoTime = timeMatch[1];

      try {
        const targetZone = timeZone || dateUtils.WORK_TIME_ZONE;
        const conflictTime = DateTime.fromISO(isoTime, {
          zone: 'utc',
        }).setZone(targetZone);

        const formattedDate = dateUtils.formatDate(conflictTime);
        const formattedTime = dateUtils.formatTime(conflictTime);

        return t('serverError.RESERVATION_CONFLICT_WITH_TIME', {
          reservationId,
          date: formattedDate,
          time: formattedTime,
        });
      } catch (error) {
        return t('serverError.RESERVATION_CONFLICT', {
          reservationId,
        });
      }
    }
    return message;
  }

  public getFieldErrors(
    t: Function,
    isRoundTrip: boolean,
    validations: ReservationValidations | null,
  ): {
    field: string;
    message: string;
  }[] {
    const fieldErrors: { field: string; message: string }[] = [];
    const genericErrorMessages: string[] = [];

    const errors: ReservationValidation[] = validations?.errors ?? [];
    const tripLegUpdates = validations?.tripLegUpdates ?? [];

    const getPickupTimeZoneForSequence = (
      tripLegSequence?: number,
    ): string | undefined => {
      if (!tripLegSequence) return undefined;
      const update = tripLegUpdates.find(
        (tl: any) => tl.tripLegSequence === tripLegSequence,
      );
      return update?.pickUpAddress?.timeZone;
    };

    errors.forEach((val) => {
      const pickupTimeZone = getPickupTimeZoneForSequence(val.tripLegSequence);
      if (
        !isRoundTrip ||
        val.tripLegSequence === 1 ||
        val.tripLegSequence === 2
      ) {
        if (val.fieldName === 'pickUpDueDateTimeUTC' && val.tripLegSequence) {
          const isConflictError = val.message
            .toLowerCase()
            .includes('conflicts with existing reservation');
          const errorMessage = isConflictError
            ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
            : t(`serverError.${val.messageId}`);

          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].PickupTime`,
            message: errorMessage,
          });
        } else if (val.fieldName === 'purposeCodeUUID' && val.tripLegSequence) {
          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].TripPurpose`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (val.fieldName === 'pickUpAddress' && val.tripLegSequence) {
          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].PickupAddress`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (val.fieldName === 'dropOffAddress' && val.tripLegSequence) {
          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].DropoffAddress`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (
          val.fieldName === 'transportationAttributes' &&
          val.tripLegSequence
        ) {
          const translatedMessage = t(`serverError.${val.messageId}`, {
            defaultValue: val.message,
          });
          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].MobilityAttribute`,
            message: translatedMessage,
          });
        } else if (
          val.fieldName === 'roundTripReturnTime' &&
          val.tripLegSequence
        ) {
          fieldErrors.push({
            field: `MultiLegTrips[${val.tripLegSequence - 1}].RoundTripReturnTime`,
            message: t(`serverError.${val.messageId}`, {
              defaultValue: val.message,
            }),
          });
        } else {
          const isConflictError = val.message
            .toLowerCase()
            .includes('conflicts with existing reservation');
          genericErrorMessages.push(
            isConflictError
              ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
              : val.message,
          );
        }
      } else if (isRoundTrip && val.tripLegSequence !== 2) {
        const isConflictError = val.message
          .toLowerCase()
          .includes('conflicts with existing reservation');
        genericErrorMessages.push(
          isConflictError
            ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
            : val.message,
        );
      }
    });

    if (genericErrorMessages.length > 0) {
      fieldErrors.push({
        field: 'GenericError',
        message: genericErrorMessages.join('\n'),
      });
    }

    return fieldErrors;
  }

  /** Like getFieldErrors, but field paths match reservationForm's tripSegmentSchema. */
  public getReservationFormFieldErrors(
    t: Function,
    isRoundTrip: boolean,
    validations: ReservationValidations | null,
  ): {
    field: string;
    message: string;
  }[] {
    const fieldErrors: { field: string; message: string }[] = [];
    const genericErrorMessages: string[] = [];

    const errors: ReservationValidation[] = validations?.errors ?? [];
    const tripLegUpdates = validations?.tripLegUpdates ?? [];

    const getPickupTimeZoneForSequence = (
      tripLegSequence?: number,
    ): string | undefined => {
      if (!tripLegSequence) return undefined;
      const update = tripLegUpdates.find(
        (tl: any) => tl.tripLegSequence === tripLegSequence,
      );
      return update?.pickUpAddress?.timeZone;
    };

    errors.forEach((val) => {
      const pickupTimeZone = getPickupTimeZoneForSequence(val.tripLegSequence);
      if (
        !isRoundTrip ||
        val.tripLegSequence === 1 ||
        val.tripLegSequence === 2
      ) {
        if (val.fieldName === 'pickUpDueDateTimeUTC' && val.tripLegSequence) {
          const isConflictError = val.message
            .toLowerCase()
            .includes('conflicts with existing reservation');
          const errorMessage = isConflictError
            ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
            : t(`serverError.${val.messageId}`);

          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].pickupDateTime`,
            message: errorMessage,
          });
        } else if (val.fieldName === 'purposeCodeUUID' && val.tripLegSequence) {
          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].reasonForTrip`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (val.fieldName === 'pickUpAddress' && val.tripLegSequence) {
          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].pickupAddress`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (val.fieldName === 'dropOffAddress' && val.tripLegSequence) {
          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].dropoffAddress`,
            message: t(`serverError.${val.messageId}`),
          });
        } else if (
          val.fieldName === 'transportationAttributes' &&
          val.tripLegSequence
        ) {
          const translatedMessage = t(`serverError.${val.messageId}`, {
            defaultValue: val.message,
          });
          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].MobilityAttribute`,
            message: translatedMessage,
          });
        } else if (
          val.fieldName === 'roundTripReturnTime' &&
          val.tripLegSequence
        ) {
          fieldErrors.push({
            field: `trips[${val.tripLegSequence - 1}].roundTripReturnDateTime`,
            message: t(`serverError.${val.messageId}`, {
              defaultValue: val.message,
            }),
          });
        } else {
          const isConflictError = val.message
            .toLowerCase()
            .includes('conflicts with existing reservation');
          genericErrorMessages.push(
            isConflictError
              ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
              : val.message,
          );
        }
      } else if (isRoundTrip && val.tripLegSequence !== 2) {
        const isConflictError = val.message
          .toLowerCase()
          .includes('conflicts with existing reservation');
        genericErrorMessages.push(
          isConflictError
            ? this.formatConflictErrorMessage(val.message, t, pickupTimeZone)
            : val.message,
        );
      }
    });

    if (genericErrorMessages.length > 0) {
      fieldErrors.push({
        field: 'GenericError',
        message: Array.from(new Set(genericErrorMessages)).join('\n'),
      });
    }

    return fieldErrors;
  }

  public reorderTripLegs(
    tripLegs: TripLeg[],
    oldIndex: number,
    newIndex: number,
  ): string[] {
    const legs = [...tripLegs];
    const legToMove = legs.splice(oldIndex, 1);
    legs.splice(newIndex, 0, legToMove[0]);
    return legs.map((l) => l.tripLegReferenceID!);
  }

  public getRoundTripStartLeg(reservation: Reservation) {
    if (reservation.tripLegs.length > 2) {
      return; // we suppose that there only two legs in round trip
    }

    return reservation.tripLegs.find((leg) => {
      return leg.metadata.roundTripReturnLeg !== true;
    });
  }

  public getApproveOrDenyTripLegsFromData(
    tripLegReferenceID: string,
    action: OnHoldTripLegApproveDenyAction,
    reservation: Reservation,
  ) {
    const isRoundTrip = this.isRoundTrip(reservation);
    const tripLegReferenceIDs = isRoundTrip
      ? reservation.tripLegs
          .filter((leg) => leg.status === ReservationStatus.OnHold)
          .map((leg) => leg.tripLegReferenceID || '')
      : [tripLegReferenceID || ''];

    return {
      passengerUUID: reservation.passengerUUID,
      reservationReferenceID: reservation.reservationReferenceID || '',
      tripLegReferenceIDs,
      action,
    };
  }

  /*
   * Get the sort time for a trip leg.
   * @param leg - The trip leg to get the sort time for. Can be a TripLeg or TransportTripLeg.
   * @returns The sort time for the trip leg.
   */
  public getLegSortTime(leg: TripLeg | TransportTripLeg): number {
    let tz;
    if ('pickUpAddress' in leg && leg.pickUpAddress) {
      tz =
        leg?.pickUpAddress?.timeZone ?? leg?.pickUpAddressTimeZoneIANA ?? 'UTC';
    } else if (
      'pickUpAddressTimeZoneIANA' in leg &&
      leg.pickUpAddressTimeZoneIANA
    ) {
      tz = leg.pickUpAddressTimeZoneIANA;
    }
    if (!tz) {
      return 0;
    }

    if ('pickUpDueDateTime' in leg && leg.pickUpDueDateTime) {
      return DateTime.fromISO(leg.pickUpDueDateTime, { zone: tz }).toMillis();
    }
    if ('pickUpDueTimeUTC' in leg && leg.pickUpDueTimeUTC) {
      return DateTime.fromISO(leg.pickUpDueTimeUTC, { zone: tz }).toMillis();
    }

    if ('appointmentTimeUTC' in leg && leg.appointmentTimeUTC) {
      return DateTime.fromISO(leg.appointmentTimeUTC, { zone: tz }).toMillis();
    }
    if ('appointmentDateTime' in leg && leg.appointmentDateTime)
      return DateTime.fromISO(leg.appointmentDateTime, { zone: tz }).toMillis();

    // If no time exists, must be will call so we default to the end of the day
    if (leg?.appointmentDate)
      return DateTime.fromISO(leg.appointmentDate, { zone: tz })
        .endOf('day')
        .toMillis();
    return 0;
  }

  public getPickupTimeBuffer(miles: number): number {
    if (miles < 10) return 45;
    if (miles < 30) return 60;
    if (miles < 45) return 75;
    if (miles < 70) return 90;
    if (miles <= 80) return 105;
    if (miles <= 100) return 120;
    if (miles <= 120) return 150;

    return 180;
  }

  public getDriverName(driver: Driver | null | undefined): string | undefined {
    if (!driver) {
      return undefined;
    }

    const toTitleCase = (name: string) =>
      name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

    return (
      [driver.driverFirstName, driver.driverMiddleName, driver.driverLastName]
        .filter(Boolean)
        .map((name) => toTitleCase(name!))
        .join(' ') || undefined
    );
  }

  public getVehicleName(
    vehicle: Vehicle | null | undefined,
  ): string | undefined {
    if (!vehicle) {
      return undefined;
    }
    return `${vehicle.vehicleMake} (${vehicle.licensePlate})` || undefined;
  }

  private isEditTripLegWindowExpired(leg: SyncedTripLeg): boolean {
    if (!leg.appointmentDate) {
      return false;
    }

    const appointmentDate = new Date(leg.appointmentDate);
    const now = new Date();

    // 1. Reset time to midnight (00:00:00) for both to ignore hours/minutes
    appointmentDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    // 2. Calculate the deadline: 2 calendar days before the appointment
    const editExpirationDate = new Date(appointmentDate);
    editExpirationDate.setDate(editExpirationDate.getDate() - 2);

    // 3. Compare dates only
    return now >= editExpirationDate;
  }

  private hasDriverAssignedToTripLeg(leg: SyncedTripLeg): boolean {
    return !!leg.driver;
  }

  private hasProviderAssignedToTripLeg(leg: SyncedTripLeg): boolean {
    return !!leg.transportationProvider;
  }

  private isTripOffered(leg: SyncedTripLeg): boolean {
    return leg.status === TripLegStatus.Offered;
  }

  public canPoralUserEditTripLeg(
    leg: SyncedTripLeg | null | undefined,
    isPortalReservation: boolean,
  ): boolean {
    // First Check is to exclude F1
    if (!isPortalReservation) {
      // This business logic only applies to portal reservations, f1 users can edit.
      return true;
    }

    if (!leg) {
      return false;
    }

    // Trip Cannot be edited if they are within 2 days
    if (this.isEditTripLegWindowExpired(leg)) {
      return false;
    }

    // Trip cannot be edidted if trip leg has driver assigned
    if (this.hasDriverAssignedToTripLeg(leg)) {
      return false;
    }

    // Trip cannot be edited if trip leg has provider assigned
    if (this.hasProviderAssignedToTripLeg(leg)) {
      return false;
    }

    // Trip cannot be edited if trip leg has offered to a provider
    if (this.isTripOffered(leg)) {
      return false;
    }

    return true;
  }

  public isAppointmentTimeAllowedForPortal(
    appointmentTime: DateTime | null | undefined,
    portalName: string | null,
  ): boolean {
    // If portal is in the list of Booking Not Allowed for next 2 days
    if (
      portalName &&
      NEXT_TWO_DAYS_APPOINTMENT_NOT_ALLOWED_PORTAL_NAMES.includes(portalName)
    ) {
      if (appointmentTime) {
        const appointmentDate = appointmentTime.startOf('day');
        const endOfToday = DateTime.now()
          .endOf('day')
          .setZone(appointmentDate.zone);
        const allowedAppointmentDate = endOfToday.plus({ days: 2 });
        if (appointmentDate <= allowedAppointmentDate) {
          return false;
        }
      }
    }
    return true;
  }

  public getTimeZoneAbbrev(
    tzCode: string | undefined,
    timeZones: TimeZone[] | undefined,
  ): string | undefined {
    if (!tzCode) return undefined;
    const dt = DateTime.now().setZone(tzCode);
    if (dt.isValid) {
      const abbr = dt.toFormat('ZZZZ');
      const tzMap: Record<string, string> = {
        EDT: 'ET',
        EST: 'ET',
        PDT: 'PT',
        PST: 'PT',
        CDT: 'CT',
        CST: 'CT',
        MDT: 'MT',
        MST: 'MT',
      };

      if (abbr) {
        return tzMap[abbr] || abbr;
      }
    }
    const tz = timeZones?.find((z) => z.ianaStandard === tzCode);
    return tz?.timeZoneName;
  }
}

const reservationUtils = new ReservationUtils();

export default reservationUtils;
